import { useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Badge, Button, Col, Form, Row } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  DropdownFilter,
  FloatInput,
  FloatSelect,
  showToast,
  WizardFooter,
} from "src/componentes";
import { ConsultaMunicipioModule } from "src/hook/Consulta/ConsultaMunicipio.Module";
import { MunicipioDto } from "src/pages/Privada/Entidade/Municipio/Municipio.Dto";
import {
  getLocal,
  MDFeInput,
  STORAGEMDFE_KEY,
  tipoDocumentoMDFeOptions,
  updateLocal,
  validarMunicipioPertenceUf,
} from "../MDFe.Dto";
import { DocumentosFormType, DocumentosSchema } from "./MDFe.Cadastro.Dto";
import MDFeModalImportarCTeView from "./Modal.ImportarCTe/MDFe.Modal.ImportarCTe.View";

interface MunicipioDescargaItemProps {
  nestIndex: number;
  control: any;
  register: any;
  errors: any;
  setValue: any;
  removeMunicipio: (index: number) => void;
  canRemoveMunicipio: boolean;
  defaultTipoDoc: number;
  codigosMunicipioRemetente: number[];
  nomesMunicipiosRemetente: string[];
  watchMunicipioDescarga?: any;
  ufFim?: string;
}

const MunicipioDescargaItem = ({
  nestIndex,
  control,
  register,
  errors,
  setValue,
  removeMunicipio,
  canRemoveMunicipio,
  defaultTipoDoc,
  codigosMunicipioRemetente,
  nomesMunicipiosRemetente,
  watchMunicipioDescarga,
  ufFim,
}: MunicipioDescargaItemProps) => {
  const consultaMunicipio = ConsultaMunicipioModule();
  const [modalImportarOpen, setModalImportarOpen] = useState(false);

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: `municipiosDescarga.${nestIndex}.documentos`,
  });

  const [localCodigoMunicipio, setLocalCodigoMunicipio] = useState<number>(
    Number(watchMunicipioDescarga?.codigoMunicipioDescarga || 0)
  );

  const codigoMunicipioDescarga =
    Number(watchMunicipioDescarga?.codigoMunicipioDescarga || 0) ||
    localCodigoMunicipio;
  const nomeMunicipioDescarga =
    watchMunicipioDescarga?.nomeMunicipioDescarga || "";

  const temMunicipioSelecionado = codigoMunicipioDescarga > 0;

  const handleAbrirModal = () => {
    if (!codigoMunicipioDescarga || codigoMunicipioDescarga <= 0) {
      showToast({
        message:
          "Por favor, selecione primeiro o município de descarga para filtrar os CT-es deste destino.",
        title: "Atenção",
        variant: "Warning",
      });
      return;
    }
    setModalImportarOpen(true);
  };

  const handleImportarCtes = (chaves: string[]) => {
    if (!chaves || chaves.length === 0) return;

    // Chaves já existentes neste município
    const existingKeys = new Set(
      fields
        .map((f: any) => (f.chaveAcesso || "").replace(/\D/g, ""))
        .filter((k: string) => k.length === 44)
    );

    const novasChaves = chaves.filter(
      (k) => !existingKeys.has(k.replace(/\D/g, ""))
    );

    if (novasChaves.length === 0) {
      showToast({
        message: "As chaves selecionadas já foram adicionadas a este município.",
        title: "Aviso",
        variant: "Warning",
      });
      return;
    }

    let startIdx = 0;
    // Se há apenas 1 linha e ela está com chave vazia, substitui a primeira
    const firstDoc = fields[0] as any;
    if (
      fields.length === 1 &&
      (!firstDoc?.chaveAcesso || String(firstDoc.chaveAcesso).trim() === "")
    ) {
      update(0, {
        tipoDocumento: 1, // CT-e
        chaveAcesso: novasChaves[0],
        indicadorReentrega: false,
        segundoCodigoBarras: "",
      });
      startIdx = 1;
    }

    for (let i = startIdx; i < novasChaves.length; i++) {
      append({
        tipoDocumento: 1, // CT-e
        chaveAcesso: novasChaves[i],
        indicadorReentrega: false,
        segundoCodigoBarras: "",
      });
    }

    showToast({
      message: `${novasChaves.length} chave(s) de CT-e importada(s) com sucesso!`,
      title: "Sucesso",
      variant: "Success",
    });
  };

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="fw-bold mb-0">
          Destino / Descarga #{nestIndex + 1}
        </h6>
        {canRemoveMunicipio && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => removeMunicipio(nestIndex)}
          >
            Remover Destino
          </Button>
        )}
      </div>

      <Row className="g-3 mb-3">
        <Col xs={12}>
          <DropdownFilter<MunicipioDto>
            fetchData={consultaMunicipio.MunicipioBuscar}
            loading={consultaMunicipio.loading}
            labelDescription="Município de Descarga (Entrega)"
            getDescription={(item: MunicipioDto) =>
              `${item.nomeMunicipio} - ${item.ufMunicipio} (IBGE: ${item.codigoMunicipio})`
            }
            getId={(item: MunicipioDto) => item.codigoMunicipio}
            registeredFields={{
              id: register(
                `municipiosDescarga.${nestIndex}.codigoMunicipioDescarga`
              ),
              description: register(
                `municipiosDescarga.${nestIndex}.nomeMunicipioDescarga`
              ),
            }}
            errorsMessageDescription={
              errors.municipiosDescarga?.[nestIndex]?.codigoMunicipioDescarga
            }
            onSelect={(item: MunicipioDto | undefined) => {
              if (item) {
                if (
                  ufFim &&
                  !validarMunicipioPertenceUf(item.codigoMunicipio, ufFim)
                ) {
                  showToast({
                    message: `O município selecionado (${item.nomeMunicipio} - ${item.ufMunicipio}) diverge da UF de descarga do MDF-e (${ufFim}) (Rejeição 612).`,
                    title: "Município Divergente (Rejeição 612)",
                    variant: "Warning",
                  });
                }
                setLocalCodigoMunicipio(Number(item.codigoMunicipio));
                setValue(
                  `municipiosDescarga.${nestIndex}.codigoMunicipioDescarga`,
                  Number(item.codigoMunicipio),
                  { shouldValidate: true }
                );
                setValue(
                  `municipiosDescarga.${nestIndex}.nomeMunicipioDescarga`,
                  item.nomeMunicipio,
                  { shouldValidate: true }
                );
              } else {
                setLocalCodigoMunicipio(0);
                setValue(
                  `municipiosDescarga.${nestIndex}.codigoMunicipioDescarga`,
                  0,
                  { shouldValidate: true }
                );
                setValue(
                  `municipiosDescarga.${nestIndex}.nomeMunicipioDescarga`,
                  "",
                  { shouldValidate: true }
                );
              }
            }}
          />
        </Col>
      </Row>

      {temMunicipioSelecionado ? (
        <div className="ps-2 ps-md-3">
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <span className="text-secondary small fw-semibold">
              Documentos Fiscais Entregues neste Município ({fields.length})
            </span>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleAbrirModal}
            >
              Importar CT-e
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() =>
                append({
                  tipoDocumento: defaultTipoDoc,
                  chaveAcesso: "",
                  indicadorReentrega: false,
                  segundoCodigoBarras: "",
                })
              }
            >
              + Adicionar Chave / Documento
            </Button>
          </div>
        </div>

        {fields.map((field, docIndex) => (
          <div key={field.id} className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="text-muted">Documento #{docIndex + 1}</small>
              {fields.length > 1 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="py-0 px-2"
                  onClick={() => remove(docIndex)}
                >
                  Excluir
                </Button>
              )}
            </div>
            <Row className="g-2">
              <Col xs={12} md={3}>
                <FloatSelect
                  label="Tipo de Doc."
                  options={tipoDocumentoMDFeOptions}
                  register={register(
                    `municipiosDescarga.${nestIndex}.documentos.${docIndex}.tipoDocumento`,
                    { valueAsNumber: true }
                  )}
                  errorsMessage={
                    errors.municipiosDescarga?.[nestIndex]?.documentos?.[
                      docIndex
                    ]?.tipoDocumento
                  }
                />
              </Col>
              <Col xs={12} md={5}>
                <FloatInput
                  label="Chave de Acesso (44 dígitos)"
                  registerField={register(
                    `municipiosDescarga.${nestIndex}.documentos.${docIndex}.chaveAcesso`
                  )}
                  errorsMessage={
                    errors.municipiosDescarga?.[nestIndex]?.documentos?.[
                      docIndex
                    ]?.chaveAcesso
                  }
                  inputMask="int"
                  maxLength={44}
                />
              </Col>
              <Col xs={12} md={3}>
                <FloatInput
                  label="2º Cód. Barras (Opcional)"
                  registerField={register(
                    `municipiosDescarga.${nestIndex}.documentos.${docIndex}.segundoCodigoBarras`
                  )}
                  errorsMessage={
                    errors.municipiosDescarga?.[nestIndex]?.documentos?.[
                      docIndex
                    ]?.segundoCodigoBarras
                  }
                  maxLength={36}
                />
              </Col>
              <Col
                xs={12}
                md={1}
                className="d-flex align-items-center justify-content-start"
              >
                <Form.Check
                  type="switch"
                  id={`reentrega-${nestIndex}-${docIndex}`}
                  label="Reentrega"
                  {...register(
                    `municipiosDescarga.${nestIndex}.documentos.${docIndex}.indicadorReentrega`
                  )}
                />
              </Col>
            </Row>
          </div>
        ))}
      </div>
    ) : (
      <div className="ps-2 ps-md-3 text-muted small fst-italic py-2">
        Selecione o município de descarga acima para informar os documentos fiscais entregues.
      </div>
    )}

    <MDFeModalImportarCTeView
        show={modalImportarOpen}
        onClose={() => setModalImportarOpen(false)}
        codigosMunicipioRemetente={codigosMunicipioRemetente}
        nomesMunicipiosRemetente={nomesMunicipiosRemetente}
        codigoMunicipioDestinatario={codigoMunicipioDescarga}
        nomeMunicipioDestinatario={nomeMunicipioDescarga}
        onImportar={handleImportarCtes}
      />
    </div>
  );
};

const MDFeCadastroDocumentosView = () => {
  const navigate = useNavigate();
  const mdfe = getLocal<MDFeInput>(STORAGEMDFE_KEY);
  const ufFim = mdfe?.ufFim;
  const defaultTipoDoc = Number(mdfe?.tipoEmitente) === 2 ? 2 : 1;

  const codigosMunicipioRemetente = useMemo(() => {
    return (mdfe?.municipiosCarregamento || [])
      .map((m) => Number(m.codigoMunicipioCarregamento))
      .filter((c) => Boolean(c) && !isNaN(c));
  }, [mdfe?.municipiosCarregamento]);

  const nomesMunicipiosRemetente = useMemo(() => {
    return (mdfe?.municipiosCarregamento || [])
      .map((m) => m.nomeMunicipioCarregamento)
      .filter(Boolean);
  }, [mdfe?.municipiosCarregamento]);

  const form = useForm<DocumentosFormType>({
    resolver: yupResolver(DocumentosSchema),
    defaultValues: {
      municipiosDescarga:
        mdfe?.municipiosDescarga && mdfe.municipiosDescarga.length > 0
          ? mdfe.municipiosDescarga
          : [
              {
                codigoMunicipioDescarga: 0,
                nomeMunicipioDescarga: "",
                documentos: [
                  {
                    tipoDocumento: defaultTipoDoc,
                    chaveAcesso: "",
                    indicadorReentrega: false,
                    segundoCodigoBarras: "",
                  },
                ],
              },
            ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "municipiosDescarga",
  });

  const watchMunicipios = form.watch("municipiosDescarga");

  // Contador em tempo real
  let totalCTe = 0;
  let totalNFe = 0;
  if (watchMunicipios && Array.isArray(watchMunicipios)) {
    watchMunicipios.forEach((m) => {
      if (m?.documentos && Array.isArray(m.documentos)) {
        m.documentos.forEach((d) => {
          if (Number(d?.tipoDocumento) === 1) totalCTe++;
          else if (Number(d?.tipoDocumento) === 2) totalNFe++;
        });
      }
    });
  }

  const handleVoltar = () => {
    navigate("../carregamento");
  };

  const onSubmit = (data: DocumentosFormType) => {
    if (ufFim) {
      let divergente = false;
      data.municipiosDescarga.forEach((m, idx) => {
        const cod = Number(m.codigoMunicipioDescarga);
        if (cod && !validarMunicipioPertenceUf(cod, ufFim)) {
          divergente = true;
          form.setError(`municipiosDescarga.${idx}.codigoMunicipioDescarga`, {
            type: "manual",
            message: `Código de Município (${cod}) diverge da UF de descarga do MDF-e (${ufFim}) (Rejeição 612).`,
          });
        }
      });
      if (divergente) {
        showToast({
          message: `Um ou mais municípios divergem da UF de descarga do MDF-e (${ufFim}) (Rejeição 612). Verifique os municípios selecionados.`,
          title: "UF de Descarga Divergente",
          variant: "Danger",
        });
        return;
      }
    }

    updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
      municipiosDescarga: data.municipiosDescarga.map((m) => ({
        codigoMunicipioDescarga: Number(m.codigoMunicipioDescarga),
        nomeMunicipioDescarga: m.nomeMunicipioDescarga,
        documentos: m.documentos.map((d) => ({
          chaveAcesso: d.chaveAcesso.replace(/\D/g, ""),
          tipoDocumento: Number(d.tipoDocumento),
          indicadorReentrega: Boolean(d.indicadorReentrega),
          segundoCodigoBarras: d.segundoCodigoBarras || undefined,
        })),
      })),
    });

    navigate("../totais");
  };

  const onError = () => {
    showToast({
      message:
        "O grupo 'Municípios de Descarga & Documentos' é obrigatório. Informe ao menos um município de descarga com chaves de acesso válidas (44 dígitos).",
      title: "Grupo Obrigatório",
      variant: "Warning",
    });
  };

  return (
    <Form noValidate onSubmit={form.handleSubmit(onSubmit, onError)} className="mt-3 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h5 className="text-primary mb-0">Municípios de Descarga & Documentos</h5>
          <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
            <Badge bg="info" text="dark">
              UF de Fim (Descarga): {ufFim || "Não informada"}
            </Badge>
            <Badge bg="primary">CT-e: {totalCTe}</Badge>
            <Badge bg="secondary">NF-e: {totalNFe}</Badge>
            <Badge bg="light" className="text-body border">
              Total de Docs: {totalCTe + totalNFe}
            </Badge>
          </div>
        </div>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() =>
            append({
              codigoMunicipioDescarga: 0,
              nomeMunicipioDescarga: "",
              documentos: [
                {
                  tipoDocumento: defaultTipoDoc,
                  chaveAcesso: "",
                  indicadorReentrega: false,
                  segundoCodigoBarras: "",
                },
              ],
            })
          }
        >
          + Adicionar Município de Descarga
        </Button>
      </div>

      {fields.map((field, index) => (
        <MunicipioDescargaItem
          key={field.id}
          nestIndex={index}
          control={form.control}
          register={form.register}
          errors={form.formState.errors}
          setValue={form.setValue}
          removeMunicipio={remove}
          canRemoveMunicipio={fields.length > 1}
          defaultTipoDoc={defaultTipoDoc}
          codigosMunicipioRemetente={codigosMunicipioRemetente}
          nomesMunicipiosRemetente={nomesMunicipiosRemetente}
          watchMunicipioDescarga={watchMunicipios?.[index]}
          ufFim={ufFim}
        />
      ))}

      <WizardFooter onVoltar={handleVoltar} />
    </Form>
  );
};

export default MDFeCadastroDocumentosView;
